import ErrorHelper from "@/utils/ErrorHelper";
import {Stack, TableCell, TableRow, TextField} from "@mui/material";
import DeleteButton from "./DeleteButton";
import {PhoneButtonType, PhoneButtonInput} from "@/api";
import MultipleLineError from "@/components/MultipleLineError";
import TargetSelector, {Target} from "@/components/TargetSelector";
import {EnumField} from "@dvrt/graphql-widgets";
import {useSortable} from "@dnd-kit/sortable";
import {CSS} from "@dnd-kit/utilities";
import {DragHandleIcon} from "@/components/widgets";
import React from "react";
import {PHONE_BUTTON_TYPE_MESSAGES} from "@/utils/translations";

export const MIN_BUTTON_POSITION = 2;

type Props = {
    errorHelper: ErrorHelper;
    sortId: number;
    index: number;
    targetNumbers: Target[];
    button: PhoneButtonInput;
    onChange: (values: Partial<PhoneButtonInput>) => void;
    onRemove: () => void;
};

const Row: React.FC<Props> = ({errorHelper, targetNumbers, sortId, button, onChange, onRemove, index}) => {
    const sortable = useSortable({id: sortId});

    const draggableStyle = {
        transform: CSS.Transform.toString(sortable.transform),
        transition: sortable.transition,
    };

    const getChangeTargetNumberNewValues = (value: number): Partial<PhoneButtonInput> => {
        const target = targetNumbers.find((item) => item.numberId === value);
        return {display: target?.displayName, targetNumberId: value, speedDialNumber: ""};
    };

    const createDisplayField = (i: number) => {
        if (button.buttonType === PhoneButtonType.Empty) return null;
        const fieldName = `buttons.${i}.display`;
        return (
            <TextField
                variant="standard"
                value={button.display || ""}
                onChange={(event) => onChange({display: event.target.value})}
                error={errorHelper.hasError(fieldName)}
                helperText={<MultipleLineError errors={errorHelper.fieldErrors(fieldName)} />}
            />
        );
    };

    const createNumberField = () => {
        // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
        switch (button.buttonType) {
            case PhoneButtonType.Blf:
                return (
                    <TargetSelector
                        selected={button.targetNumberId}
                        handleChange={(numberId) => {
                            const values = getChangeTargetNumberNewValues(numberId);
                            onChange(values);
                        }}
                        targets={targetNumbers}
                        errors={errorHelper.fieldErrors(`buttons.${index}.targetNumberId`)}
                        required
                        variant="standard"
                    />
                );
            case PhoneButtonType.Line:
                return (
                    <TargetSelector
                        selected={button.targetNumberId}
                        handleChange={(numberId) => {
                            const values = getChangeTargetNumberNewValues(numberId);
                            onChange(values);
                        }}
                        targets={targetNumbers}
                        errors={errorHelper.fieldErrors(`buttons.${index}.targetNumberId`)}
                        required
                        variant="standard"
                    />
                );
            case PhoneButtonType.SpeedDial:
                return (
                    <TextField
                        variant="standard"
                        value={button.speedDialNumber || ""}
                        onChange={(event) => onChange({speedDialNumber: event.target.value})}
                        error={errorHelper.hasError(`buttons.${index}.speedDialNumber`)}
                        helperText={
                            <MultipleLineError errors={errorHelper.fieldErrors(`buttons.${index}.speedDialNumber`)} />
                        }
                    />
                );
            default:
                return null;
        }
    };

    const changeButtonType = (value: PhoneButtonType) => {
        let values: Partial<PhoneButtonInput>;
        switch (value) {
            case PhoneButtonType.Blf:
                values = getChangeTargetNumberNewValues(targetNumbers[0]?.numberId);
                break;
            case PhoneButtonType.BlfGroup:
                values = {speedDialNumber: "", targetNumberId: 0};
                break;
            case PhoneButtonType.SpeedDial:
                values = {targetNumberId: 0};
                break;
            case PhoneButtonType.Line:
                values = getChangeTargetNumberNewValues(targetNumbers[0]?.numberId);
                break;
            case PhoneButtonType.Empty:
            case PhoneButtonType.Callpark:
                values = {targetNumberId: 0, speedDialNumber: "", display: ""};
                break;
        }
        onChange({...values, buttonType: value});
    };

    return (
        <TableRow style={draggableStyle} ref={sortable.setNodeRef}>
            <TableCell>
                <Stack direction="row" spacing={1} alignItems="center">
                    <DragHandleIcon
                        setActivatorNodeRef={sortable.setActivatorNodeRef}
                        attributes={sortable.attributes}
                        listeners={sortable.listeners}
                    />
                    <span>{button.buttonPosition}</span>
                </Stack>
            </TableCell>
            <TableCell>
                <EnumField
                    variant="standard"
                    value={button.buttonType}
                    onChange={(value) => changeButtonType(value as PhoneButtonType)}
                    enum_={{PhoneButtonType}}
                    translations={PHONE_BUTTON_TYPE_MESSAGES}
                />
            </TableCell>
            <TableCell>{createNumberField()}</TableCell>
            <TableCell>{createDisplayField(index)}</TableCell>
            <TableCell>
                <DeleteButton removeRow={onRemove} />
            </TableCell>
        </TableRow>
    );
};

export default Row;
